import { escapeHtmlAttr } from '../../../utils/escapeHtmlAttr'
import { type ComposerMenuItem } from './types'

export const MENTION_TRIGGER_REGEX = /(^|\s)@(\w*)$/

/**
 * Slash command trigger: `/` at start or after whitespace.
 * Query is a single token (no spaces) so prose like `check /tmp folder now` is not
 * treated as an in-progress command — avoids replacing trailing text with a chip.
 */
export const SLASH_TRIGGER_REGEX = /(^|\s)\/(\S*)$/

/**
 * Index of the trigger character under the caret, or null when it is mid-token
 * (URL path, email, date, etc.). `regex` must capture the leading boundary in group 1.
 */
export const getTriggerIndex = (text: string, cursorPos: number, regex: RegExp): number | null => {
  const match = regex.exec(text.slice(0, cursorPos))
  if (match == null) return null
  return match.index + match[1].length
}

/** Text typed after the trigger character, or null when no trigger is under the caret. */
export const getTriggerQuery = (text: string, cursorPos: number, regex: RegExp): string | null => {
  const triggerIndex = getTriggerIndex(text, cursorPos, regex)
  if (triggerIndex == null) return null
  return text.slice(triggerIndex + 1, cursorPos)
}

export const getMentionQuery = (text: string, cursorPos: number): string | null =>
  getTriggerQuery(text, cursorPos, MENTION_TRIGGER_REGEX)

export const getSlashTriggerIndex = (text: string, cursorPos: number): number | null =>
  getTriggerIndex(text, cursorPos, SLASH_TRIGGER_REGEX)

export const getSlashQuery = (text: string, cursorPos: number): string | null =>
  getTriggerQuery(text, cursorPos, SLASH_TRIGGER_REGEX)

/** Case-insensitive match on `name` or any alias. */
export const filterComposerMenuItems = <T extends ComposerMenuItem>(items: readonly T[], query: string): T[] => {
  const normalizedQuery = query.toLowerCase()
  return items.filter((item) =>
    [item.name, ...(item.aliases ?? [])].some((token) => token.toLowerCase().includes(normalizedQuery))
  )
}

export type TriggerMenuNavResult = 'handled' | 'select' | 'close' | 'none'

/**
 * Arrow / Enter / Escape handling for composer trigger menus. Focus stays in the
 * contenteditable, so highlighting is tracked by index rather than DOM focus.
 * Caller performs select/close; arrows update `setHighlightedIndex` here.
 */
export const navigateTriggerMenu = (
  event: { key: string; preventDefault: () => void },
  itemCount: number,
  setHighlightedIndex: (updater: (prev: number) => number) => void
): TriggerMenuNavResult => {
  if (itemCount <= 0) {
    return 'none'
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    setHighlightedIndex((prev) => (prev + 1) % itemCount)
    return 'handled'
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    setHighlightedIndex((prev) => (prev - 1 + itemCount) % itemCount)
    return 'handled'
  }
  if (event.key === 'Enter') {
    return 'select'
  }
  if (event.key === 'Escape') {
    event.preventDefault()
    return 'close'
  }
  return 'none'
}

/**
 * Select the in-progress trigger token (`@query` / `/query`) under the caret so a
 * following `insertHTML` replaces it. Returns false when no trigger matches `regex`.
 */
export const selectTriggerTokenAtCaret = (selection: Selection, regex: RegExp): boolean => {
  if (selection.rangeCount === 0) return false
  const range = selection.getRangeAt(0)
  let container: Node = range.startContainer

  if (container.nodeType !== Node.TEXT_NODE && container.childNodes.length > 0) {
    container = container.childNodes[Math.max(0, range.startOffset - 1)] ?? container.childNodes[0]
  }
  if (container.nodeType !== Node.TEXT_NODE) return false

  const text = container.textContent ?? ''
  const cursorOffset = container === range.startContainer ? range.startOffset : text.length
  const triggerIndex = getTriggerIndex(text, cursorOffset, regex)
  if (triggerIndex == null) return false

  const selectRange = document.createRange()
  selectRange.setStart(container, triggerIndex)
  selectRange.setEnd(container, cursorOffset)
  selection.removeAllRanges()
  selection.addRange(selectRange)
  return true
}

/** Clone of the current selection range when it lies inside `editor`, otherwise null. */
export const getSelectionRangeInside = (editor: HTMLElement): Range | null => {
  const selection = globalThis.getSelection()
  if (selection == null || selection.rangeCount === 0) return null
  const range = selection.getRangeAt(0)
  return editor.contains(range.startContainer) ? range.cloneRange() : null
}

/**
 * Put the caret back at `range` when it still lies inside `editor` (nodes may have
 * been removed since), otherwise at the end of the editor content.
 */
export const restoreCaret = (editor: HTMLElement, selection: Selection, range: Range | null): void => {
  let target = range
  if (target == null || !editor.contains(target.startContainer)) {
    target = document.createRange()
    target.selectNodeContents(editor)
    target.collapse(false)
  }
  selection.removeAllRanges()
  selection.addRange(target)
}

/** Snapshot of the chips with `className` currently in `editor`, taken before inserting a new one. */
export const getChipElements = (editor: HTMLElement, className: string): ReadonlySet<Element> =>
  new Set(editor.querySelectorAll(`.${className}`))

/**
 * Move the caret to the end of the text node after the chip that is not in `existingChips`
 * (the one just inserted), so inserting before an existing chip does not jump to the last one.
 */
export const placeCaretAfterNewChip = (
  editor: HTMLElement,
  selection: Selection,
  className: string,
  existingChips: ReadonlySet<Element>
): void => {
  const insertedChip = Array.from(editor.querySelectorAll(`.${className}`)).find((chip) => !existingChips.has(chip))
  const cursorTarget = insertedChip?.nextSibling
  if (cursorTarget == null) return
  const newRange = document.createRange()
  newRange.setStart(cursorTarget, cursorTarget.textContent?.length ?? 1)
  newRange.collapse(true)
  selection.removeAllRanges()
  selection.addRange(newRange)
}

export const getCaretCharacterOffset = (element: HTMLElement): number => {
  const selection = globalThis.getSelection()
  if (selection == null || selection.rangeCount === 0) return 0
  const range = selection.getRangeAt(0)
  const preCaretRange = range.cloneRange()
  preCaretRange.selectNodeContents(element)
  preCaretRange.setEnd(range.startContainer, range.startOffset)
  return preCaretRange.toString().length
}

const stripHtmlTags = (html: string): string => {
  let result = ''
  let lastIndex = 0
  let i = html.indexOf('<')

  while (i !== -1) {
    const close = html.indexOf('>', i + 2)
    if (close === -1) break
    result += html.slice(lastIndex, i)
    lastIndex = close + 1
    i = html.indexOf('<', lastIndex)
  }

  return result + html.slice(lastIndex)
}

export const MENTION_CHIP_CLASS_NAME = 'mention'

/** Marker class for slash-command chips (caret placement); styles are inline on the chip. */
export const SLASH_COMMAND_CHIP_CLASS_NAME = 'inline-chip'

export const buildMentionChipHtml = (mention: { id: string | number; name: string }): string =>
  `<span class="${MENTION_CHIP_CLASS_NAME}" data-mention-id="${mention.id}" contenteditable="false">@${mention.name}</span>\u00A0`

/**
 * Slash-command chip HTML: non-editable pill with id for extraction on submit.
 * Display text is the command title (no leading `/`); markdown still serializes as `/{name}`.
 * `cssText` is the chip's inline style (see `getInlineChipCssText`).
 */
export const buildSlashCommandChipHtml = (command: { id: string | number; name: string }, cssText = ''): string => {
  const id = escapeHtmlAttr(String(command.id))
  const name = escapeHtmlAttr(command.name)
  const style = cssText === '' ? '' : ` style="${escapeHtmlAttr(cssText)}"`
  return (
    `<span class="${SLASH_COMMAND_CHIP_CLASS_NAME}"${style} data-slash-command-id="${id}" ` +
    `data-slash-command-name="${name}" contenteditable="false">${name}</span>\u00A0`
  )
}

const EMPHASIS_TAG = '(?:b|strong|i|em)'
const EMPHASIS_WHITESPACE = '(?:\\s|&nbsp;)+'
const LEADING_EMPHASIS_WHITESPACE_REGEX = new RegExp(`(<${EMPHASIS_TAG}>)(${EMPHASIS_WHITESPACE})`, 'gi')
const TRAILING_EMPHASIS_WHITESPACE_REGEX = new RegExp(`(${EMPHASIS_WHITESPACE})(</${EMPHASIS_TAG}>)`, 'gi')
const EMPTY_EMPHASIS_REGEX = /<(b|strong|i|em)><\/\1>/gi

/**
 * Move whitespace from inside `<i>`/`<b>` to outside (`<i>hello </i>` → `<i>hello</i> `).
 * Contenteditable often keeps the trailing space in the formatted run, and markdown
 * does not close emphasis after a space (`_hello _` renders literally). Repeats so
 * nested tags (`<b><i>x </i></b>`) are fully unwrapped; empty tags are dropped.
 */
const moveWhitespaceOutOfEmphasis = (html: string): string => {
  let previous: string
  let current = html
  do {
    previous = current
    current = current
      .replace(LEADING_EMPHASIS_WHITESPACE_REGEX, '$2$1')
      .replace(TRAILING_EMPHASIS_WHITESPACE_REGEX, '$2$1')
      .replace(EMPTY_EMPHASIS_REGEX, '')
  } while (current !== previous)
  return current
}

export const htmlToMarkdown = (html: string): string => {
  const converted = moveWhitespaceOutOfEmphasis(html)
    .replace(/<span[^>]*data-mention-id="([^"]*)"[^>]*>@([^<]*)<\/span>/gi, '@[user:$1]')
    // Prefer explicit name attr (pill chips); fall back to slash-prefixed inner text.
    .replace(/<span[^>]*data-slash-command-name="([^"]*)"[^>]*>[\s\S]*?<\/span>/gi, '/$1')
    .replace(/<span[^>]*data-slash-command-id="[^"]*"[^>]*>\/([^<]*)<\/span>/gi, '/$1')
    .replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**')
    .replace(/<strong>([\s\S]*?)<\/strong>/gi, '**$1**')
    .replace(/<i>([\s\S]*?)<\/i>/gi, '_$1_')
    .replace(/<em>([\s\S]*?)<\/em>/gi, '_$1_')
    .replace(/<a[^>]+href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<div>([\s\S]*?)<\/div>/gi, '$1\n')
    .replace(/<p>([\s\S]*?)<\/p>/gi, '$1\n')

  // `&amp;` is decoded last so escaped entities (e.g. `&amp;lt;`) stay literal.
  return stripHtmlTags(converted)
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .trim()
}

const SLASH_COMMAND_ID_REGEX = /data-slash-command-id="([^"]*)"/gi

/**
 * Collect slash-command chip ids from composer HTML (order of first appearance).
 * Ids are kept as strings; callers map them to Flow integer skill ids when pinning.
 */
export const extractSlashCommandIds = (html: string): string[] => {
  const ids: string[] = []
  const seen = new Set<string>()
  SLASH_COMMAND_ID_REGEX.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = SLASH_COMMAND_ID_REGEX.exec(html)) !== null) {
    const id = match[1]
    if (id === '' || seen.has(id)) {
      continue
    }
    seen.add(id)
    ids.push(id)
  }
  return ids
}

export const preventBlur = (e: React.MouseEvent): void => {
  e.preventDefault()
}
